/**
 * Sentry Error Tracking Service
 *
 * Initializes and configures Sentry for error tracking and monitoring.
 * Provides utilities for error reporting, user context, and performance monitoring.
 *
 * @module services/sentry
 */

import * as Sentry from '@sentry/browser';

/**
 * Sentry configuration options
 */
export interface SentryConfig {
  /** Sentry DSN (Data Source Name) */
  dsn?: string;
  /** Environment name (development, staging, production) */
  environment?: string;
  /** Release version */
  release?: string;
  /** Sample rate for transactions (0.0 to 1.0) */
  tracesSampleRate?: number;
  /** Sample rate for session replay (0.0 to 1.0) */
  replaysSessionSampleRate?: number;
  /** Sample rate for error replay (0.0 to 1.0) */
  replaysOnErrorSampleRate?: number;
  /** Whether to enable debug mode */
  debug?: boolean;
}

/**
 * Default Sentry configuration
 */
const DEFAULT_CONFIG: Required<SentryConfig> = {
  dsn: import.meta.env.VITE_SENTRY_DSN || '',
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || 'development',
  release: `grist-geoportail@${import.meta.env.VITE_APP_VERSION || '2.0.0'}`,
  tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  debug: !import.meta.env.PROD,
};

/**
 * Initialize Sentry error tracking
 *
 * @param config - Optional configuration to override defaults
 * @returns Whether Sentry was successfully initialized
 */
export function initSentry(config: Partial<SentryConfig> = {}): boolean {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  // Skip initialization if no DSN is provided
  if (!finalConfig.dsn) {
    console.warn('Sentry DSN not configured. Error tracking disabled.');
    return false;
  }

  try {
    Sentry.init({
      dsn: finalConfig.dsn,
      environment: finalConfig.environment,
      release: finalConfig.release,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      tracesSampleRate: finalConfig.tracesSampleRate,
      replaysSessionSampleRate: finalConfig.replaysSessionSampleRate,
      replaysOnErrorSampleRate: finalConfig.replaysOnErrorSampleRate,
      debug: finalConfig.debug,

      // Filter out known non-critical errors
      beforeSend(event, hint) {
        const error = hint.originalException;

        // Filter out network errors in development
        if (!import.meta.env.PROD && error instanceof Error) {
          if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
            return null;
          }
        }

        return event;
      },

      // Ignore specific errors
      ignoreErrors: [
        // Browser extension errors
        'Non-Error promise rejection captured',
        // Network errors that are expected
        'NetworkError',
        'Failed to fetch',
        // ResizeObserver loop errors (harmless)
        'ResizeObserver loop limit exceeded',
      ],
    });

    console.info('Sentry initialized successfully');
    return true;
  } catch (error) {
    console.error('Failed to initialize Sentry:', error);
    return false;
  }
}

/**
 * Set user context for error tracking
 *
 * @param userId - Unique user identifier
 * @param email - User email (optional)
 * @param username - Username (optional)
 */
export function setUserContext(userId: string, email?: string, username?: string): void {
  Sentry.setUser({
    id: userId,
    email,
    username,
  });
}

/**
 * Clear user context
 */
export function clearUserContext(): void {
  Sentry.setUser(null);
}

/**
 * Set custom context for error tracking
 *
 * @param key - Context key
 * @param value - Context value
 */
export function setContext(key: string, value: Record<string, unknown>): void {
  Sentry.setContext(key, value);
}

/**
 * Add breadcrumb for debugging
 *
 * @param message - Breadcrumb message
 * @param category - Breadcrumb category
 * @param level - Severity level
 * @param data - Additional data
 */
export function addBreadcrumb(
  message: string,
  category: string = 'default',
  level: Sentry.SeverityLevel = 'info',
  data?: Record<string, unknown>
): void {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
  });
}

/**
 * Capture an exception manually
 *
 * @param error - Error to capture
 * @param context - Additional context
 */
export function captureException(error: Error, context?: Record<string, unknown>): void {
  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value as Record<string, unknown>);
      });
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
}

/**
 * Capture a message
 *
 * @param message - Message to capture
 * @param level - Severity level
 * @param context - Additional context
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = 'info',
  context?: Record<string, unknown>
): void {
  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value as Record<string, unknown>);
      });
      Sentry.captureMessage(message, level);
    });
  } else {
    Sentry.captureMessage(message, level);
  }
}

/**
 * Start a performance transaction
 *
 * @param name - Transaction name
 * @param operation - Operation type
 * @returns Transaction object or span
 */
export function startTransaction(name: string, operation: string) {
  return Sentry.startSpan(
    {
      name,
      op: operation,
    },
    (span) => span
  );
}

/**
 * Add tags to errors
 *
 * @param key - Tag key
 * @param value - Tag value
 */
export function setTag(key: string, value: string): void {
  Sentry.setTag(key, value);
}

/**
 * Set multiple tags at once
 *
 * @param tags - Tags object
 */
export function setTags(tags: Record<string, string>): void {
  Sentry.setTags(tags);
}

// Export Sentry for advanced usage
export { Sentry };
